/**
 * SidebarOffsetTuner — shared layout tuner. Sets global CSS variables consumed
 * by ALL THREE editors (Flow, Synth VR, Synth Console) and persists them to
 * localStorage so a tuned layout stays identical everywhere:
 *   --sidebar-x     horizontal distance (px) of every sidebar from the edge
 *                   (SR: left/right; Flow/Console: shell right padding).
 *   --scrollbar-x   distance (px) of every floating OverlayScrollbar from its edge.
 *   --scrollbar-y   vertical inset (px) that scales the OverlayScrollbar track.
 *   --shell-gap     gap between a sidebar's inner edge and the canvas.
 *   --shell-pad-left  left sidebar's distance from the screen edge.
 *   --shell-pad-y   sidebars' top + bottom margin.
 *
 * The DEFAULT_* values below are the tuned baseline; localStorage overrides them
 * per-browser while tuning. Keep this component mounted in every editor so the
 * same values apply consistently — see App.tsx.
 */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

const SIDEBAR_KEY = 'synapse:sidebar-x';
const SCROLLBAR_KEY = 'synapse:scrollbar-x';
const SCROLLBAR_Y_KEY = 'synapse:scrollbar-y';
const SHELL_GAP_KEY = 'synapse:shell-gap';
const SHELL_PAD_LEFT_KEY = 'synapse:shell-pad-left';
const SHELL_PAD_Y_KEY = 'synapse:shell-pad-y';
// Tuned baseline (saved 2026-06-24) — shared across all three editors.
const SIDEBAR_DEFAULT = 12;
const SCROLLBAR_DEFAULT = 0;
const SCROLLBAR_Y_DEFAULT = 53;
const SHELL_GAP_DEFAULT = 3;
const SHELL_PAD_LEFT_DEFAULT = 12;
const SHELL_PAD_Y_DEFAULT = 21;

function readInt(key: string, def: number): number {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  const n = raw == null ? NaN : parseInt(raw, 10);
  return Number.isFinite(n) ? n : def;
}

/** Apply both saved offsets to the document root (call at startup). */
export function applySidebarX(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--sidebar-x', `${readInt(SIDEBAR_KEY, SIDEBAR_DEFAULT)}px`);
  document.documentElement.style.setProperty('--scrollbar-x', `${readInt(SCROLLBAR_KEY, SCROLLBAR_DEFAULT)}px`);
  document.documentElement.style.setProperty('--scrollbar-y', `${readInt(SCROLLBAR_Y_KEY, SCROLLBAR_Y_DEFAULT)}px`);
  document.documentElement.style.setProperty('--shell-gap', `${readInt(SHELL_GAP_KEY, SHELL_GAP_DEFAULT)}px`);
  document.documentElement.style.setProperty('--shell-pad-left', `${readInt(SHELL_PAD_LEFT_KEY, SHELL_PAD_LEFT_DEFAULT)}px`);
  document.documentElement.style.setProperty('--shell-pad-y', `${readInt(SHELL_PAD_Y_KEY, SHELL_PAD_Y_DEFAULT)}px`);
}

export default function SidebarOffsetTuner() {
  const [sidebar, setSidebar] = useState<number>(() => readInt(SIDEBAR_KEY, SIDEBAR_DEFAULT));
  const [scrollbar, setScrollbar] = useState<number>(() => readInt(SCROLLBAR_KEY, SCROLLBAR_DEFAULT));
  const [scrollbarY, setScrollbarY] = useState<number>(() => readInt(SCROLLBAR_Y_KEY, SCROLLBAR_Y_DEFAULT));
  const [shellGap, setShellGap] = useState<number>(() => readInt(SHELL_GAP_KEY, SHELL_GAP_DEFAULT));
  const [padLeft, setPadLeft] = useState<number>(() => readInt(SHELL_PAD_LEFT_KEY, SHELL_PAD_LEFT_DEFAULT));
  const [padY, setPadY] = useState<number>(() => readInt(SHELL_PAD_Y_KEY, SHELL_PAD_Y_DEFAULT));

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-x', `${sidebar}px`);
    try { localStorage.setItem(SIDEBAR_KEY, String(sidebar)); } catch { /* quota */ }
  }, [sidebar]);

  useEffect(() => {
    document.documentElement.style.setProperty('--scrollbar-x', `${scrollbar}px`);
    try { localStorage.setItem(SCROLLBAR_KEY, String(scrollbar)); } catch { /* quota */ }
    // OverlayScrollbar reads --scrollbar-x during its measure pass; nudge it to
    // re-measure so the change shows live.
    window.dispatchEvent(new Event('resize'));
  }, [scrollbar]);

  useEffect(() => {
    document.documentElement.style.setProperty('--scrollbar-y', `${scrollbarY}px`);
    try { localStorage.setItem(SCROLLBAR_Y_KEY, String(scrollbarY)); } catch { /* quota */ }
    window.dispatchEvent(new Event('resize'));
  }, [scrollbarY]);

  useEffect(() => {
    document.documentElement.style.setProperty('--shell-gap', `${shellGap}px`);
    try { localStorage.setItem(SHELL_GAP_KEY, String(shellGap)); } catch { /* quota */ }
    window.dispatchEvent(new Event('resize'));
  }, [shellGap]);

  useEffect(() => {
    document.documentElement.style.setProperty('--shell-pad-left', `${padLeft}px`);
    try { localStorage.setItem(SHELL_PAD_LEFT_KEY, String(padLeft)); } catch { /* quota */ }
    window.dispatchEvent(new Event('resize'));
  }, [padLeft]);

  useEffect(() => {
    document.documentElement.style.setProperty('--shell-pad-y', `${padY}px`);
    try { localStorage.setItem(SHELL_PAD_Y_KEY, String(padY)); } catch { /* quota */ }
    window.dispatchEvent(new Event('resize'));
  }, [padY]);

  return ReactDOM.createPortal(
    <div style={panelStyle}>
      <div style={titleStyle}>Layout tuner · temp</div>
      <div style={rowStyle}>
        <span style={labelStyle}>Sidebar X</span>
        <input type="range" min={0} max={48} step={1} value={sidebar}
          onChange={e => setSidebar(Number(e.target.value))} style={sliderStyle} />
        <span style={valStyle}>{sidebar}px</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Scrollbar X</span>
        <input type="range" min={0} max={24} step={1} value={scrollbar}
          onChange={e => setScrollbar(Number(e.target.value))} style={sliderStyle} />
        <span style={valStyle}>{scrollbar}px</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Scrollbar Y</span>
        <input type="range" min={0} max={80} step={1} value={scrollbarY}
          onChange={e => setScrollbarY(Number(e.target.value))} style={sliderStyle} />
        <span style={valStyle}>{scrollbarY}px</span>
      </div>
      <div style={dividerStyle} />
      <div style={rowStyle}>
        <span style={labelStyle}>Inner gap</span>
        <input type="range" min={0} max={64} step={1} value={shellGap}
          onChange={e => setShellGap(Number(e.target.value))} style={sliderStyle} />
        <span style={valStyle}>{shellGap}px</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Margin left</span>
        <input type="range" min={0} max={64} step={1} value={padLeft}
          onChange={e => setPadLeft(Number(e.target.value))} style={sliderStyle} />
        <span style={valStyle}>{padLeft}px</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Margin top/bottom</span>
        <input type="range" min={0} max={64} step={1} value={padY}
          onChange={e => setPadY(Number(e.target.value))} style={sliderStyle} />
        <span style={valStyle}>{padY}px</span>
      </div>
    </div>,
    document.body,
  );
}

const panelStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 16,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 99998,
  width: 320,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '12px 14px',
  borderRadius: 16,
  background: 'oklch(0.10 calc(0.018 * var(--tint-c)) var(--tint-h) / 0.92)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.4)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow: '0 18px 50px rgba(0, 0, 0, 0.6)',
  fontFamily: "'Montserrat', sans-serif",
  color: '#E8E2F5',
};

const titleStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
  color: 'var(--accent)',
};

const dividerStyle: React.CSSProperties = { height: 1, background: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.18)', margin: '2px 0' };
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10 };
const labelStyle: React.CSSProperties = { minWidth: 104, fontSize: 11, fontWeight: 600, color: '#B8B0D0', whiteSpace: 'nowrap' };
const sliderStyle: React.CSSProperties = { flex: 1, accentColor: 'var(--accent)' as React.CSSProperties['accentColor'] };
const valStyle: React.CSSProperties = { minWidth: 38, textAlign: 'right', fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums' };
